import React from 'react';
import { Button, createStyles, Grid, makeStyles, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { AxiosError, AxiosResponse } from 'axios';

import { ClientModel, clientModelInitial } from '../../../../Models/ClientModel';
import { clipperDataInitial, ClipperModel } from '../../../../Models/ClipperModel';
import { NewsModel, newsModelInitial } from '../../../../Models/NewsModel';
import { ClassificationModel } from '../../../../Models/ClassificationModel';
import ClientService from '../../../../Services/ClientService';
import ClipperService from '../../../../Services/ClipperService';
import { PageLocation } from '../../../../shared/enum/PageLocation';
import { HomeContext } from '../../../Home/HomeContext';
import { LoadingStatus } from '../../enum/LoadingStatus';
import { NewsContext } from '../../NewsContext';
import { AuthContext } from '../../../Authentication/AuthContext';
import { classificationModelInitial } from '../../../../Models/ClassificationModel';
import { DetachedClassificationAction } from '../../enum/DetachedClassificationAction';


const useStyles = makeStyles(() => createStyles({
    button: {
        width: '100%',
        textAlign: 'right'
    }
})
);

export function DetachedClassificationInitial() {
    const classes = useStyles();

    const homeContext = React.useContext(HomeContext);
    const authContext = React.useContext(AuthContext);
    const newsContext = React.useContext(NewsContext);

    const [clientList, setClientList] = React.useState<ClientModel[]>([]);
    const [clipperList, setClipperList] = React.useState<ClipperModel[]>([]);
    const [selectedClient, setSelectedClient] = React.useState<ClientModel>(clientModelInitial);
    const [selectedClipper, setSelectedClipper] = React.useState<ClipperModel>(clipperDataInitial);

    const [clientNotSelectedError, setClientNotSelectedError] = React.useState<boolean>(false);
    const [clipperNotSelectedError, setClipperNotSelectedError] = React.useState<boolean>(false);

    const [clientLoading, setClientLoading] = React.useState<LoadingStatus>(LoadingStatus.NOT_STARTED);
    const [clipperLoading, setClipperLoading] = React.useState<LoadingStatus>(LoadingStatus.NOT_STARTED);

    React.useEffect(() => {
        if (clientLoading == LoadingStatus.LOADING ||
            clipperLoading == LoadingStatus.LOADING) {

            homeContext.setOpenLoadingDialog(true);
        }
        
        if (clientLoading == LoadingStatus.DONE &&
            clipperLoading == LoadingStatus.DONE) {

            setClientLoading(LoadingStatus.NOT_STARTED);
            setClipperLoading(LoadingStatus.NOT_STARTED);
            homeContext.setOpenLoadingDialog(false);
        }
    }, [clientLoading, clipperLoading]);

    React.useEffect(() => {
        if (homeContext.pageLocation === PageLocation.DETACHED_CLASSIFICATION_INITIAL) {
            clearDeatchedClassification();
            fetchClientList();
            fetchClipperList();
        }
    }, [homeContext.pageLocation]);

    const fetchClientList = () => {
        setClientLoading(LoadingStatus.LOADING);
        ClientService.clientByUserService(authContext.login.user.id)
            .getAll()
            .then((res: AxiosResponse) => setClientList(res.data))
            .catch((error: AxiosError) => homeContext.showError('Ops... Ocorreu um erro e não foi possível carregar os clientes.' +
                    'Por favor, tente novamente dentro de alguns instantes. <br/>', error))
            .finally(() => setClientLoading(LoadingStatus.DONE));
    };

    const fetchClipperList = () => {
        setClipperLoading(LoadingStatus.LOADING);
        ClipperService.clipperService()
            .getAll()
            .then((res: AxiosResponse) => setClipperList(res.data))
            .catch((error: AxiosError) => homeContext.showError('Ops... Ocorreu um erro e não foi possível carregar as clipadoras.' +
                    'Por favor, tente novamente dentro de alguns instantes. <br/>', error))
            .finally(() => setClipperLoading(LoadingStatus.DONE));
    };

    const clearDeatchedClassification = () => {
        newsContext.setNews(newsModelInitial);
        newsContext.setClassification(classificationModelInitial);
        newsContext.setDetachedNewsDataAction(DetachedClassificationAction.CLEAR);
        newsContext.setDetachedNewsCategorizationAction(DetachedClassificationAction.CLEAR);
    };
    
    const hasInitialError = () => {
        const errorClient = !selectedClient || selectedClient.id < 1;
        const errorClipper = !selectedClipper || selectedClipper.id < 1;
        setClientNotSelectedError(errorClient);
        setClipperNotSelectedError(errorClipper);

        return errorClient || errorClipper;
    };

    const handleIncludeNews = () => {
        if(!hasInitialError()) {
            const news: NewsModel = JSON.parse(JSON.stringify(newsContext.news));
            news.clientId = selectedClient.id;
            news.clientDescription = selectedClient.name;
            news.clientCodeMetric = selectedClient.codeMetric;
            news.clipperId = selectedClipper.id;

            const classification: ClassificationModel = JSON.parse(JSON.stringify(newsContext.classification));
            classification.clientId = selectedClient.id;
            classification.clientName = selectedClient.name;
            classification.clientCodeMetric = selectedClient.codeMetric;
            classification.startClassification = new Date();

            newsContext.setNews(news);
            newsContext.setClassification(classification);
            homeContext.setPageLocation(PageLocation.DETACHED_CLASSIFICATION);
        }
    };

    const getClient = () => {
        return (
            <Grid item xs={6}>
                <Autocomplete
                    id="client"
                    value={selectedClient}
                    onChange={(_, value: ClientModel | null) => {
                        setSelectedClient(value ? value : clientModelInitial);
                    }}
                    options={clientList}
                    getOptionLabel={(option: ClientModel) => option.name}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderInput={(params) =>
                        <TextField
                            {...params}
                            label="Cliente"
                            variant="outlined"
                            error={clientNotSelectedError && (selectedClient.id < 1)}
                            helperText={clientNotSelectedError && (selectedClient.id < 1) ? 'Campo obrigatório' : ''}/>
                    } />
            </Grid>
        );
    };

    const getClipper = () => {
        return (
            <Grid item xs={6}>
                <Autocomplete
                    id="client"
                    value={selectedClipper}
                    onChange={(_, value: ClipperModel | null) => {
                        setSelectedClipper(value ? value : clipperDataInitial);
                    }}
                    options={clipperList}
                    getOptionLabel={(option: ClipperModel) => option.name}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderInput={(params) =>
                        <TextField
                            {...params}
                            label="Clipadora"
                            variant="outlined"
                            error={clipperNotSelectedError && (selectedClipper.id < 1)}
                            helperText={clipperNotSelectedError && (selectedClipper.id < 1) ? 'Campo obrigatório' : ''}/>
                    } />
            </Grid>
        );
    };

    const getIncludeNewsButton = () => {
        return (
            <Grid item className={classes.button}>
                <Button
                    onClick={handleIncludeNews}
                    variant='contained'
                    color='primary'
                    size='large'>
                    INCLUIR MATÉRIA
                </Button>
            </Grid>
        );
    };

    return (
        <>
            { getClient() }
            { getClipper() }
            { getIncludeNewsButton() }
        </>
    );
}